<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
    modelValue: {
        type: [File, null],
        default: null
    },
    currentUrl: {
        type: String,
        default: null
    },
    accept: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['update:modelValue'])

const fileInput = ref(null)

const fileSize = computed(() => {
    const bytes = props.modelValue?.size ?? 0
    if (bytes < 1024 * 1024) {
        return `${Math.max(1, Math.round(bytes / 1024))} KB`
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
})

watch(() => props.modelValue, (file) => {
    if (!file && fileInput.value) {
        fileInput.value.value = null
    }
})

const pick = () => {
    fileInput.value?.click()
}

const onChange = (e) => {
    emit('update:modelValue', e.target.files?.[0] || null)
}

const clear = () => {
    emit('update:modelValue', null)
}
</script>

<template>
    <div>
        <input
            ref="fileInput"
            type="file"
            class="sr-only"
            :accept="accept"
            @change="onChange"
        />

        <div
            v-if="modelValue"
            class="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
        >
            <i class="fa-regular fa-paperclip text-gray-400"></i>
            <div class="min-w-0 flex-1">
                <p class="truncate text-sm text-gray-800">{{ modelValue.name }}</p>
                <p class="text-xs text-gray-500">{{ fileSize }}</p>
            </div>
            <button
                type="button"
                class="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                :title="_mft('shared:action.remove')"
                @click="clear"
            >
                <i class="fa-regular fa-xmark"></i>
                <span class="sr-only">{{ _mft('shared:action.remove') }}</span>
            </button>
        </div>

        <div
            v-else-if="currentUrl"
            class="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
        >
            <i class="fa-regular fa-paperclip text-gray-400"></i>
            <a
                :href="currentUrl"
                target="_blank"
                class="mf-link min-w-0 flex-1 truncate text-sm"
            >
                {{ _mft('crm:leadNoteCard.viewAttachment') }}
            </a>
            <button
                type="button"
                class="text-sm font-medium text-gray-600 hover:text-gray-900"
                @click="pick"
            >
                {{ _mft('crm:leadNoteCard.replaceFile') }}
            </button>
        </div>

        <button
            v-else
            type="button"
            class="flex w-full items-center gap-3 rounded-lg border border-dashed border-gray-300 px-3 py-3 text-left transition-colors hover:border-gray-400 hover:bg-gray-50"
            @click="pick"
        >
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <i class="fa-regular fa-paperclip"></i>
            </span>
            <span>
                <span class="block text-sm font-medium text-gray-800">{{ _mft('crm:leadNoteCard.chooseFile') }}</span>
                <span class="block text-xs text-gray-500">{{ _mft('crm:leadNoteCard.allowedFileTypes') }}</span>
            </span>
        </button>
    </div>
</template>
